import { useState, useCallback, useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@/components/layout/PageLayout';
import { HiredRequisitionsProvider } from '@/features/onboarding/entry';
import { JobSearchBar } from '@/features/home/components/JobSearchBar';
import { HomeJobListItem } from '@/features/home/components/HomeJobListItem';
import { HomeJobDetailPanel } from '@/features/home/components/HomeJobDetailPanel';
import { DataPrivacyDialog } from '@/features/home/components/DataPrivacyDialog';
import { useIsMobile, useIsSplitLayout } from '@/hooks/useMediaQuery';
import { Toast } from '@/features/home/components/Toast';
import { analyticsService } from '@/services/analytics/analyticsService';
import { FilterDropdown, SortDropdown } from './components/JobFilters';
import { ResumeAnalyzerDialog } from './components/ResumeAnalyzerDialog';
import { ViewToggle } from './components/ViewToggle';
import { EmailIcon, LinkedInIcon, WhatsAppIcon, XIcon } from './components/SocialIcons';
import { useJobsList } from './hooks/useJobsList';
import { useOpenApplication } from './hooks/useOpenApplication';
import { useTenantStore } from '@/store/tenantStore';
import { useAuthStore } from '@/store/authStore';
import { countAppliedFilters, hasSearchTerms } from './utils/filterState';
import { shareToSocialPlatform } from './utils/jobHelpers';
import { setRelevanceData } from './utils/relevanceData';
import {
  APPLICATION_PAGE,
  JOB_SHARE_REF,
  captureJobLinkOrigin,
  setApplicationPageAndType,
} from '@/features/jobs/utils/jobOrigin';
import { FILE_ACCEPT_STRING } from '@/types/application.types';
import { ROUTES } from '@/app/router/routes';
import type { JobEntity } from '@/types/job.types';
import { InfoTip } from '@/components/feedback/InfoTip';
import styles from './JobsPage.module.css';
import { internalJobHref } from './components/JobCardParts';

/**
 * JobsPage — search results. Mirrors Flutter JobListView.
 *
 * Layout follows the Career Portal redesign (same as the Home jobs section):
 * 1. Heading + search card (Upload Resume, Skills/Roles search, location, filters, sort)
 * 2. Results toolbar (All/Hot Jobs or the category criteria, total, card/list toggle)
 * 3. Card view: job list + sticky preview of the selected job (desktop); below the split
 *    breakpoint selecting a job opens its page. List view: one full-width card per row.
 *    Without the preview, the cards carry Flutter's Apply / Easy Apply / Applied actions.
 */

const SHIMMER_JOBS = 4;

// Flutter WebUploadResume texts
const UPLOAD_TOOLTIP = 'We accept DOC, DOCX,\n TXT, PDF, WPS less than 5MB';
const UPLOAD_WORK_TEXT =
  'Skills are extracted from the resume to connect you with the right opportunities';

// ---------------------------------------------------------------------------
// No Match Widget — Flutter JobListNoMatchWidget
// ---------------------------------------------------------------------------

// Flutter JobListNoMatchWidget texts, plus the variants used when they wouldn't be true
const NO_MATCH_TITLE = 'No jobs match your current criteria';
const NO_JOBS_TITLE = 'No jobs available';
const NO_MATCH_PROFILE = 'There are no active roles aligned with your profile at the moment, but new opportunities open up fast!';
const NO_MATCH_RESUME = 'There are no active roles matching your resume at the moment, but new opportunities open up fast!';
const NO_MATCH_SEARCH = 'There are no active roles matching your search at the moment, but new opportunities open up fast!';
const NO_JOBS = 'There are no active roles at the moment, but new opportunities open up fast!';

const SOCIAL_PLATFORMS = [
  { key: 'linkedin', label: 'LinkedIn', icon: <LinkedInIcon /> },
  { key: 'x', label: 'X', icon: <XIcon /> },
  { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon /> },
  { key: 'email', label: 'Email', icon: <EmailIcon /> },
];

/**
 * Flutter shows the same "aligned with your profile" text and profile tip to everyone, even to a
 * signed-out visitor looking at an unfiltered list. Here:
 *   - `hasCriteria` (a search, filter, category or Hot Jobs is applied): "No jobs match your
 *     current criteria" and "Browse All Openings" to start over; without one the list was already
 *     every opening, so the title is "No jobs available" and the button is left out;
 *   - `isResumeMatch` (/search_by_resume): the profile text for a signed-in candidate, a resume
 *     text for a visitor; otherwise a search text, or a plain one without criteria;
 *   - the "Keep your profile updated" tip only for a signed-in candidate.
 */
export function NoMatchWidget({ hasCriteria, isResumeMatch = false }: {
  hasCriteria: boolean;
  isResumeMatch?: boolean;
}) {
  const { t } = useTranslation();
  const preferences = useTenantStore((s) => s.preferences);
  const isSignedIn = useAuthStore((s) => !!s.user);
  const navigate = useNavigate();
  const socialLink = (preferences as Record<string, unknown>)?.noMatchingRoleSocialLink as string | undefined;

  const handleShare = (platform: string) => {
    if (socialLink) {
      shareToSocialPlatform(platform, socialLink);
    }
  };

  // Flutter: navigateTo(jobsRoute, isReplace: true) — a fresh, unfiltered list. JobsPage
  // remounts on every navigation, so the search starts over.
  const handleBrowseAll = () => {
    void analyticsService.logScreenView({ screenName: ROUTES.JOBS });
    navigate(ROUTES.JOBS, { replace: true });
  };

  return (
    <div className={styles.noMatch}>
      <div className={styles.noMatchIcon} aria-hidden="true">
        <svg width="26" height="26" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <circle cx="9" cy="9" r="6" />
          <path d="M13.5 13.5 17 17M6.5 9h5" />
        </svg>
      </div>
      <h2 className={styles.noMatchTitle}>{t(hasCriteria ? NO_MATCH_TITLE : NO_JOBS_TITLE)}</h2>
      <p className={styles.noMatchDesc}>
        {t(
          isResumeMatch
            ? isSignedIn ? NO_MATCH_PROFILE : NO_MATCH_RESUME
            : hasCriteria ? NO_MATCH_SEARCH : NO_JOBS,
        )}
      </p>
      {isSignedIn && (
        <p className={styles.noMatchTip}>
          <span className={styles.noMatchTipLabel}>{t('Tip:')}</span>
          {t('Keep your profile updated to stay ahead.')}
        </p>
      )}

      <div className={styles.noMatchShare}>
        <p>{t('Know friends who might be a fit? Share our openings with your network')}</p>
        <div className={styles.socialIcons}>
          {SOCIAL_PLATFORMS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={styles.socialIconBtn}
              onClick={() => handleShare(p.key)}
              title={`Share on ${p.label}`}
              aria-label={`Share on ${p.label}`}
            >
              {p.icon}
            </button>
          ))}
        </div>
      </div>

      {hasCriteria && (
        <button className={styles.browseAllBtn} onClick={handleBrowseAll} type="button">
          {t('Browse All Openings')}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shimmer Skeleton
// ---------------------------------------------------------------------------

export function JobListShimmer({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={styles.shimmerCard} aria-hidden="true">
          <span className={`${styles.shimmerLine} ${styles.shimmerTitle}`} />
          <span className={`${styles.shimmerLine} ${styles.shimmerMeta}`} />
          <div className={styles.shimmerTags}>
            <span className={`${styles.shimmerLine} ${styles.shimmerTag}`} />
            <span className={`${styles.shimmerLine} ${styles.shimmerTag}`} />
            <span className={`${styles.shimmerLine} ${styles.shimmerTag}`} />
          </div>
          <span className={`${styles.shimmerLine} ${styles.shimmerFooter}`} />
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// JobsPage
// ---------------------------------------------------------------------------

/**
 * Every navigation to /jobs builds a new page, as Flutter's navigateTo does (e.g. "Browse All
 * Openings" replaces /jobs with a fresh list), so the content is keyed on the history entry.
 */
export function JobsPage() {
  const location = useLocation();
  return <JobsPageContent key={location.key} />;
}

function JobsPageContent() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { state, actions } = useJobsList();
  const isSplit = useIsSplitLayout();
  const isMobile = useIsMobile();
  const config = useTenantStore((s) => s.config);
  const enableEasyApply = !!(config as Record<string, unknown> | null)?.enableEasyApply;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // channel / sourceName / ref → referral headers + application origin (Flutter jobsRoute,
  // which handles /jobs and /jobs/:id alike)
  useEffect(() => {
    captureJobLinkOrigin(searchParams);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    searchInput,
    entities,
    aggregations,
    filterAggregations,
    sortAggregation,
    totalJobsCount,
    isCardView,
    isLoadingJobList,
    isLoadingMore,
    isLoading,
    toast,
    categoryDetails,
    showDataPrivacy,
    isAnalyzing,
  } = state;

  const hasResults = entities.length > 0;
  const hasAggregations = aggregations.length > 0;
  const showResults = hasAggregations && hasResults;
  const showNoMatch = !isLoading && !isLoadingJobList && entities.length === 0;
  // Anything narrowing the list — without it, an empty list means there are no openings at all
  const hasCriteria =
    hasSearchTerms(searchInput) ||
    !!searchInput.locations ||
    !!searchInput.isHotJob ||
    !!searchInput.categoryDetails ||
    countAppliedFilters(searchInput.filters ?? {}) > 0;
  // Card view keeps the reference list + preview layout; list view drops the preview
  const showPreview = isSplit && isCardView;

  // Keep a valid selection: default to the first job, and fall back when results change
  const selectedIndex = Math.max(
    0,
    entities.findIndex((e) => (e.displayId ?? e.id) === selectedId),
  );
  const selected = entities[selectedIndex];

  // Flutter JobListView.buildJobCard onTapTitle: new tab with the internal-link ref
  const handleJobTitleClick = useCallback((entity: JobEntity) => {
    setApplicationPageAndType(APPLICATION_PAGE.CAREER_SEARCH, false);
    setRelevanceData(entity);
    void analyticsService.logScreenView({ screenName: ROUTES.JOBS, screenClass: 'job_list_page' });
    window.open(`${ROUTES.JOBS}/${entity.displayId ?? ''}?ref=${JOB_SHARE_REF.INTERNAL}`, '_blank');
  }, []);

  // Flutter JobListView.buildJobCard onApply(index, isEasyApply)
  const openApplication = useOpenApplication();
  const handleJobApplyClick = useCallback((entity: JobEntity, isEasyApply: boolean) => {
    setApplicationPageAndType(APPLICATION_PAGE.CAREER_SEARCH, false);
    openApplication(entity.displayId, isEasyApply, 'job_list_page');
  }, [openApplication]);

  const handleSelect = (entity: JobEntity) => {
    if (!showPreview) {
      handleJobTitleClick(entity);
      return;
    }
    setSelectedId(entity.displayId ?? entity.id ?? null);
  };

  // ── Upload Resume ──
  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);
  const handleResumeFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) actions.onResumeSelected(file);
  };

  // ── Results header — Flutter JobListView._buildBody ──
  const categoryLabel = categoryDetails?.displayKey
    ? t(`${categoryDetails.displayKey.charAt(0).toUpperCase()}${categoryDetails.displayKey.slice(1)}`)
    : '';
  const categoryValue =
    categoryDetails?.value === 'lateral' ? t('Experienced') : categoryDetails?.displayValue ?? '';

  return (
    <PageLayout>
      {/* Candidate onboarding (React only, FLUTTER_DEVIATIONS §5): the jobs the candidate is hired for, loaded once for the
          Onboarding In Progress buttons on the cards and the detail panel; nothing in prod */}
      <HiredRequisitionsProvider>
        <div className={styles.container}>
          {/* Hidden file input for Upload Resume */}
          <input
            ref={fileInputRef}
            type="file"
            accept={FILE_ACCEPT_STRING}
            style={{ display: 'none' }}
            onChange={handleResumeFileChange}
          />
          {showDataPrivacy && (
            <DataPrivacyDialog
              onAgree={() => actions.onDataPrivacyAgree(openFilePicker)}
              onCancel={actions.onDataPrivacyCancel}
            />
          )}
          {isAnalyzing && <ResumeAnalyzerDialog />}

          {/* Heading */}
          <div className={styles.sectionHeading}>
            <span className={styles.accent} />
            <h1 className={styles.sectionTitle}>
              {t('Elevate Your Job Search')}{' '}
              <span className={styles.titleHighlight}>{t(' With AI').trim()}</span>
            </h1>
          </div>

          {/* Upload Resume + search + filters */}
          <div className={styles.searchCard}>
            <div className={styles.searchRow}>
              <button
                type="button"
                className={styles.uploadBtn}
                onClick={() => actions.onClickUploadResume(openFilePicker)}
                disabled={isAnalyzing}
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10 13V3M6 7l4-4 4 4M4 13v3a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-3" />
                </svg>
                {t('Upload Resume')}
                <span className={styles.uploadInfo}><InfoTip message={t(UPLOAD_TOOLTIP)} /></span>
              </button>
              <div className={styles.searchBar}>
                <JobSearchBar
                  searchInput={searchInput}
                  onSearch={actions.onClickGo}
                  variant="inline"
                  showSwitch
                />
              </div>
            </div>
            <p className={styles.uploadHint}>{t(UPLOAD_WORK_TEXT)}</p>

            {showResults && (
              <div className={styles.filterRow}>
                <span className={styles.filterLabel}>{t('Filters:')}</span>
                <div className={styles.filterChips}>
                  {filterAggregations.map((agg) => (
                    <FilterDropdown
                      key={agg.name}
                      aggregation={agg}
                      onChange={actions.onFilterSelection}
                      onApply={actions.onApplyFilters}
                      onClear={actions.onClearFilter}
                    />
                  ))}
                </div>
                <div className={styles.filterActions}>
                  {sortAggregation && (
                    <SortDropdown aggregation={sortAggregation} onSelect={actions.onSelectSort} />
                  )}
                  <button
                    className={styles.filterActionBtn}
                    onClick={actions.onClearFilters}
                    type="button"
                  >
                    {t('Clear all')}
                  </button>
                  <button
                    className={`${styles.filterActionBtn} ${styles.filterApply}`}
                    onClick={actions.onApplyFilters}
                    type="button"
                  >
                    {t('Apply Filters')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Results toolbar */}
          {showResults && (
            <div className={styles.toolbar}>
              <div className={styles.toolbarTitle}>
                {categoryDetails ? (
                  <>
                    <h2 className={styles.resultsTitle}>{t('Jobs matches for your search criteria')}</h2>
                    <span className={styles.categoryChip}>
                      <span className={styles.categoryChipLabel}>{categoryLabel}</span>
                      <span aria-hidden="true">-</span>
                      <span>{categoryValue}</span>
                    </span>
                  </>
                ) : (
                  <h2 className={styles.resultsTitle}>
                    {t(searchInput.isHotJob ? 'Hot' : 'All')} {t('Jobs')}
                  </h2>
                )}
              </div>
              <div className={styles.toolbarEnd}>
                <p className={styles.count}>
                  {t('Total Jobs')}: {totalJobsCount}
                </p>
                {!isMobile && <ViewToggle isCardView={isCardView} onToggle={actions.onToggleCardView} />}
              </div>
            </div>
          )}

          {/* Job list + selected job preview, or loading / empty state */}
          {showNoMatch ? (
            <NoMatchWidget hasCriteria={hasCriteria} />
          ) : (
            <div className={showPreview ? styles.split : undefined}>
              <div className={styles.listCol}>
                <div className={styles.list}>
                  {isLoadingJobList ? (
                    <JobListShimmer count={SHIMMER_JOBS} />
                  ) : (
                    <>
                      {entities.map((entity, index) => (
                        <HomeJobListItem
                          key={entity.displayId ?? entity.id ?? index}
                          entity={entity}
                          isSelected={showPreview && index === selectedIndex}
                          onSelect={() => handleSelect(entity)}
                          onSave={() => actions.onSaveJob(entity, index)}
                          onUnsave={() => actions.onUnsaveJob(entity, index)}
                          enableEasyApply={enableEasyApply}
                          onApply={(isEasyApply) => handleJobApplyClick(entity, isEasyApply)}
                          alwaysShowActions={!showPreview}
                          titleLink={{
                            href: internalJobHref(entity.displayId),
                            newTab: true,
                            onOpen: () => handleJobTitleClick(entity),
                          }}
                        />
                      ))}
                      {isLoadingMore && <JobListShimmer count={2} />}
                    </>
                  )}
                </div>
              </div>

              {showPreview && (
                <div className={styles.detailCol}>
                  {isLoadingJobList ? (
                    <div className={styles.shimmerDetail} aria-hidden="true" />
                  ) : selected ? (
                    <HomeJobDetailPanel
                      key={selected.displayId ?? selected.id}
                      entity={selected}
                      onSave={() => actions.onSaveJob(selected, selectedIndex)}
                      onUnsave={() => actions.onUnsaveJob(selected, selectedIndex)}
                      onClickApply={(isEasyApply) => handleJobApplyClick(selected, isEasyApply)}
                      onViewDetails={() => handleJobTitleClick(selected)}
                    />
                  ) : null}
                </div>
              )}
            </div>
          )}

          {/* Toast */}
          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={actions.clearToast}
            />
          )}
        </div>
      </HiredRequisitionsProvider>
    </PageLayout>
  );
}
